import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { ApiResponse, PagedResponse, SearchParams } from './types';

// --- Quote Document (matches backend QuoteDocument : BaseDocument) ---

export interface Quote {
  id: string;
  tenant_id: string;
  customer_id: string;
  contact_id: string;
  opportunity_id: string;
  quote_number: string;
  title: string;
  description: string;
  status: string;
  valid_until: string | null;
  subtotal: number;
  discount_percentage: number;
  discount_amount: number;
  tax_percentage: number;
  tax_amount: number;
  total: number;
  currency: string;
  payment_terms: string;
  notes: string;
  tags: string[];
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface QuoteLineItem {
  id: string;
  tenant_id: string;
  quote_id: string;
  product_name: string;
  description: string;
  quantity: number;
  unit_price: number;
  discount_percentage: number;
  line_total: number;
  sort_order: number;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Request DTOs ---

export interface CreateQuoteRequest {
  customer_id: string;
  contact_id?: string;
  opportunity_id?: string;
  quote_number?: string;
  title: string;
  description?: string;
  valid_until?: string;
  discount_percentage?: number;
  discount_amount?: number;
  tax_percentage?: number;
  currency?: string;
  payment_terms?: string;
  notes?: string;
  tags?: string[];
}

export interface UpdateQuoteRequest {
  customer_id: string;
  contact_id?: string;
  opportunity_id?: string;
  quote_number?: string;
  title: string;
  description?: string;
  status?: string;
  valid_until?: string;
  discount_percentage?: number;
  discount_amount?: number;
  tax_percentage?: number;
  currency?: string;
  payment_terms?: string;
  notes?: string;
  tags?: string[];
}

export interface CreateQuoteLineItemRequest {
  product_name: string;
  description?: string;
  quantity: number;
  unit_price: number;
  discount_percentage?: number;
  sort_order?: number;
}

export interface UpdateQuoteLineItemRequest {
  product_name: string;
  description?: string;
  quantity: number;
  unit_price: number;
  discount_percentage?: number;
  sort_order?: number;
}

// --- Filter params for quote list ---

export interface QuoteFilters {
  customer_id?: string;
  contact_id?: string;
  opportunity_id?: string;
  status?: string;
  currency?: string;
}

// --- Quote API functions ---

const BASE = '/quotes';

export async function listQuotes(
  params: SearchParams,
  filters?: QuoteFilters
): Promise<PagedResponse<Quote>> {
  const query: Record<string, unknown> = {
    from: params.from ?? 0,
    size: params.size ?? 20,
  };
  if (params.keyword) query.keyword = params.keyword;
  if (params.sort_field) query.sort_field = params.sort_field;
  if (params.sort_descending !== undefined) query.sort_descending = params.sort_descending;
  if (filters?.customer_id) query.customer_id = filters.customer_id;
  if (filters?.contact_id) query.contact_id = filters.contact_id;
  if (filters?.opportunity_id) query.opportunity_id = filters.opportunity_id;
  if (filters?.status) query.status = filters.status;
  if (filters?.currency) query.currency = filters.currency;

  const response = await apiGet<PagedResponse<Quote>>(BASE, query);
  if (!response.data) {
    return { items: [], total: 0, page: 1, page_size: 20, total_pages: 0 };
  }
  return response.data;
}

export async function getQuote(id: string): Promise<Quote> {
  const response = await apiGet<Quote>(`${BASE}/${id}`);
  if (!response.data) {
    throw new Error(response.message || 'Quote not found');
  }
  return response.data;
}

export async function createQuote(data: CreateQuoteRequest): Promise<Quote> {
  const response = await apiPost<Quote>(BASE, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to create quote');
  }
  return response.data;
}

export async function updateQuote(id: string, data: UpdateQuoteRequest): Promise<Quote> {
  const response = await apiPut<Quote>(`${BASE}/${id}`, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to update quote');
  }
  return response.data;
}

export async function deleteQuote(id: string): Promise<ApiResponse<{ id: string }>> {
  return apiDelete<{ id: string }>(`${BASE}/${id}`);
}

// --- Line Item API functions ---

export async function listQuoteLineItems(quoteId: string): Promise<QuoteLineItem[]> {
  const response = await apiGet<QuoteLineItem[]>(`${BASE}/${quoteId}/line-items`);
  if (!response.data) {
    return [];
  }
  return response.data;
}

export async function createQuoteLineItem(
  quoteId: string, data: CreateQuoteLineItemRequest
): Promise<QuoteLineItem> {
  const response = await apiPost<QuoteLineItem>(`${BASE}/${quoteId}/line-items`, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to create line item');
  }
  return response.data;
}

export async function updateQuoteLineItem(
  quoteId: string, itemId: string, data: UpdateQuoteLineItemRequest
): Promise<QuoteLineItem> {
  const response = await apiPut<QuoteLineItem>(`${BASE}/${quoteId}/line-items/${itemId}`, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to update line item');
  }
  return response.data;
}

export async function deleteQuoteLineItem(
  quoteId: string, itemId: string
): Promise<ApiResponse<{ id: string }>> {
  return apiDelete<{ id: string }>(`${BASE}/${quoteId}/line-items/${itemId}`);
}
